import { EvidenceCard, SubHead } from "./EvidenceCard";
import { DataTable } from "@/components/DataTable";
import { MetricRow, BarPair } from "@/components/SiteChrome";
import { AcosPill } from "@/components/Pills";
import { fmtMoney, fmtPct, fmtNum } from "@/lib/format";

const AD_TYPE_LABELS = { sp: "Sponsored Products", sb: "Sponsored Brands", sd: "Sponsored Display" };

export const AdMixSection = ({ data, cur, badge, footnote }) => {
  const missing = data.missing || [];
  return (
    <EvidenceCard id="adMix" title="Ad type mix" badge={badge} badgeColor="#0d0d0d" footnote={footnote}>
      <p className="text-sm text-[color:var(--grey)] mt-1 mb-6 max-w-2xl">
        Sales share by ad type vs the {data.benchmark?.sp}/{data.benchmark?.sb}/{data.benchmark?.sd} benchmark.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(data.types || {}).map(([k, t]) => (
          <div key={k} className="border-2 border-[color:var(--ink)] p-5 bg-white" data-testid={`ad-mix-${k}`}>
            <p className="ss-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink)] mb-3">
              {AD_TYPE_LABELS[k] || k}
            </p>
            <BarPair label="Sales share" actual={t.salesSharePct} bench={t.benchmarkSharePct} color="#007d62" />
            <dl className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-[color:var(--grey)]">Spend</dt><dd className="ss-mono">{fmtMoney(cur, t.spend)}</dd></div>
              <div className="flex justify-between"><dt className="text-[color:var(--grey)]">Sales</dt><dd className="ss-mono">{fmtMoney(cur, t.sales)}</dd></div>
              <div className="flex justify-between items-center"><dt className="text-[color:var(--grey)]">ACoS</dt><dd><AcosPill value={t.acos} /></dd></div>
            </dl>
          </div>
        ))}
      </div>
      {missing.length > 0 && (
        <p className="mt-6 text-xs text-[color:var(--grey)] leading-relaxed" data-testid="ad-mix-missing">
          Not running in your bulk file: {missing.map((m) => AD_TYPE_LABELS[m] || m).join(", ")}.
        </p>
      )}
    </EvidenceCard>
  );
};

const PLACEMENT_LABELS = { top_of_search: "Top of search", rest_of_search: "Rest of search", product_pages: "Product pages" };

export const PlacementSection = ({ data, cur, badge, footnote }) => (
  <EvidenceCard id="placement" title="Placement efficiency" badge={badge} badgeColor="#007d62" footnote={footnote}>
    <div className="grid grid-cols-1 sm:grid-cols-3 border-t-2 border-[color:var(--ink)] my-6 divide-x divide-[color:var(--hairline)]" data-testid="placement-cards">
      {Object.entries(data.byPlacement || {}).map(([k, p]) => (
        <div key={k} className="p-5 bg-white">
          <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--grey)] mb-2">{PLACEMENT_LABELS[k] || k}</p>
          <p className="ss-mono text-2xl font-semibold mt-1">{fmtMoney(cur, p.spend)}</p>
          <p className="text-xs text-[color:var(--grey)]">spend</p>
          <dl className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between items-center"><dt className="text-[color:var(--grey)]">ACoS</dt><dd><AcosPill value={p.acos} /></dd></div>
            <div className="flex justify-between"><dt className="text-[color:var(--grey)]">CVR</dt><dd className="ss-mono">{fmtPct(p.cvr, 1)}</dd></div>
          </dl>
        </div>
      ))}
    </div>
    {(data.campaigns || []).length > 0 && (
      <>
        <SubHead>Top campaigns</SubHead>
        <DataTable
          testId="placement-campaigns-table"
          cap={10}
          rows={data.campaigns}
          columns={[
            { key: "campaign", label: "Campaign" },
            { key: "spend", label: "Spend", align: "right", render: (r) => fmtMoney(cur, r.spend) },
            { key: "sales", label: "Sales", align: "right", render: (r) => fmtMoney(cur, r.sales) },
            { key: "acos", label: "ACoS", align: "right", render: (r) => <AcosPill value={r.acos} /> },
          ]}
        />
      </>
    )}
  </EvidenceCard>
);

const ACTION_COLOR = {
  Scale: "#007d62",
  Maintain: "#565b57",
  Optimise: "#e8a13c",
  Pause: "#d6453d",
};

const ActionTag = ({ value }) => {
  if (!value) return "—";
  const color = ACTION_COLOR[value] || "#565b57";
  return (
    <span
      className="ss-mono text-[11px] font-semibold uppercase tracking-[0.14em] px-2 py-1 border-2"
      style={{ borderColor: color, color }}
    >
      {value}
    </span>
  );
};

export const PortfolioSection = ({ data, cur, badge, footnote }) => (
  <EvidenceCard id="portfolio" title="Products" badge={badge} badgeColor="#007d62" footnote={footnote}>
    <MetricRow
      testId="portfolio-metrics"
      items={[
        { label: "Advertised products", value: fmtNum(data.productsTotal) },
        { label: "Inefficient spend", value: fmtPct(data.inefficientSpendPct, 1), color: data.inefficientSpendPct > 30 ? "#d6453d" : "#007d62", sub: "above 35% ACoS or no sales" },
      ]}
    />
    <DataTable
      testId="portfolio-table"
      cap={15}
      totalCount={data.productsTotal}
      rows={data.products || []}
      columns={[
        { key: "asin", label: "ASIN" },
        { key: "sku", label: "SKU" },
        { key: "spend", label: "Spend", align: "right", render: (r) => fmtMoney(cur, r.spend) },
        { key: "sales", label: "Sales", align: "right", render: (r) => fmtMoney(cur, r.sales) },
        { key: "orders", label: "Orders", align: "right", render: (r) => fmtNum(r.orders) },
        { key: "acos", label: "ACoS", align: "right", render: (r) => <AcosPill value={r.acos} /> },
        { key: "action", label: "Action", render: (r) => <ActionTag value={r.action} /> },
      ]}
    />
  </EvidenceCard>
);
